import { Injectable } from '@angular/core';
import { LoggingService } from '../../logging/logic/logging.service';
import { ActionHandler, ActionHandlerType } from '../action-handler';
import { DynFormsService } from '../../dyn-forms/logic/dyn-forms.service';
import { AppService } from '../../app-service/logic/app.service';
import { CloudSyncState } from 'src/app/_model/dyn-form-data';
import { ActivityConfirmation } from 'src/app/_model/activity-confirmation';
import { ActivityConfirmationsService } from '../../activity-confirmations/logic/activity-confirmations.service';
import { DraftSyncEventsService } from '../../dyn-forms/draft-sync-events.service';

/**
 * The flat AC infos the backend has always returned after a dynform upload.
 * Used for the DynFormData update, which must keep working against backends
 * that do not send the `acdto` key yet.
 */
interface UploadedAcInfo {
  formDataId: number;
  acId: number;
  acukey: string;
  /** KRK-45: stable identity of the AC chain, see DynFormData.chainRootAcId */
  chainRootAcId: number | undefined;
  acStatus: number | undefined;
  acIsLocked: boolean | undefined;
  draftOwnerEmpId: number | undefined;
  draftOwnerMcId: number | undefined;
  draftOwnerServiceJobId: number | undefined;
  isConflictVersion: boolean;
}

/**
 * Handles the AC infos the backend returns after a dynform upload (the whole
 * response is merged into this action's params, see CachedFifoRemoteActionService).
 *
 * Does two things, in this order:
 * 1. writes the AC infos onto the local DynFormData (APP4-948 cloud sync state)
 * 2. persists the AC the backend just created, so the draft handling knows about
 *    it before the next AC sync - the backend only attaches the AC to the
 *    ExtFile, and ExtFiles of a type with synctype `never` never reach the app
 *
 * The target was renamed from `updateacukey`; the old one is still served by
 * LegacyUpdateAcuKeyActionHandler below.
 */
@Injectable({
  providedIn: 'root',
})
export class UpdateActivityConfirmationActionHandler implements ActionHandler {
  // Public Member
  public readonly name: ActionHandlerType = 'updateactivityconfirmation';

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private dynFormsService: DynFormsService,
    private appService: AppService,
    private activityConfirmationsService: ActivityConfirmationsService,
    private draftSyncEvents: DraftSyncEventsService
  ) {}

  // ActionHandler
  public async executeAction(params?: any): Promise<any> {
    this.logger.log(
      this.constructor.name,
      'start UpdateActivityConfirmationActionHandler with params',
      params
    );

    /*
     * KRK-45: the document was deleted while this upload sat in the offline queue. The backend
     * kept the payload as a deleted conflict version, but there is nothing here left to write
     * AC infos onto - the local row goes instead, and the AC is not stored either (it carries no
     * draft owner and would only sit in the local table until the next sync).
     */
    if (this.toBoolean(params?.['isdraftdeleted'])) {
      await this.discardDeletedDraft(params);
      return;
    }

    const acInfo = this.parseParams(params);
    if (acInfo != undefined) {
      await this.updateDynFormData(acInfo);
    }
    // Deliberately NOT inside the block above: the AC is backend-owned and worth storing
    // even when the DynFormData could not be updated. The two used to share an early
    // return, so one missing flat key silently cost us both.
    await this.storeActivityConfirmation(params?.['acdto']);
  }

  // Private Functions
  /**
   * The AC identity of the upload.
   *
   * Read from `acdto` first, with the flat top-level keys only as a fallback. The acdto is
   * the authoritative record (same shape the sync delivers); the flat keys are the legacy
   * variant. Relying on the flat keys alone made the whole write-back depend on them being
   * present: without acukey/acid on the local DynFormData the NEXT upload of that form
   * carries no chain identity, and the backend then allocates a new number range value -
   * so one edited document ends up as two separate draft chains.
   */
  private parseParams(params?: any): UploadedAcInfo | undefined {
    const acdto = params?.['acdto'];
    const formDataId = +(params?.['formDataId'] ?? NaN);
    const acId = +(acdto?.['id'] ?? params?.['acid'] ?? NaN);
    const acukey = acdto?.['acukey'] ?? params?.['acukey'];
    // KRK-45: only on the acdto - the legacy flat keys never carried it
    const chainRootAcId = acdto?.['chainrootacid'];

    if (isNaN(formDataId) || isNaN(acId) || acukey == undefined) {
      this.logger.error(
        this.constructor.name,
        `Cannot update acukey as not all values are available formDataId=${formDataId}` +
          ` acid=${acId} acukey=${acukey} acdto present=${acdto != undefined}`
      );
      return undefined;
    }

    const status = acdto?.['status'] ?? params?.['acstatus'];
    const isLocked = acdto?.['islocked'] ?? params?.['acislocked'];
    const draftOwnerEmpId = acdto?.['draftownerempid'] ?? params?.['draftownerempid'];
    const draftOwnerMcId = acdto?.['draftownermcid'] ?? params?.['draftownermcid'];
    const draftOwnerServiceJobId =
      acdto?.['draftownerservicejobid'] ?? params?.['draftownerservicejobid'];
    const isConflictVersion =
      acdto?.['isconflictversion'] ?? params?.['isconflictversion'];

    return {
      formDataId,
      acId,
      acukey,
      chainRootAcId: chainRootAcId != null ? +chainRootAcId : undefined,
      acStatus: status != null ? +status : undefined,
      acIsLocked: isLocked != null ? this.toBoolean(isLocked) : undefined,
      draftOwnerEmpId: draftOwnerEmpId != null ? +draftOwnerEmpId : undefined,
      draftOwnerMcId: draftOwnerMcId != null ? +draftOwnerMcId : undefined,
      draftOwnerServiceJobId:
        draftOwnerServiceJobId != null ? +draftOwnerServiceJobId : undefined,
      isConflictVersion: this.toBoolean(isConflictVersion),
    };
  }

  /**
   * KRK-45: removes the local row of a chain the backend reports as deleted, and tells an open
   * form/list about it.
   *
   * Matched on the chain identity from the acdto, with the formDataId as the fallback: the acdto
   * of a deleted conflict version is the only place the chain root arrives here.
   */
  private async discardDeletedDraft(params: any): Promise<void> {
    const acdto = params?.['acdto'];
    const chainRootAcId = acdto?.['chainrootacid'] != undefined ? +acdto['chainrootacid'] : undefined;
    const acukey = acdto?.['acukey'] ?? params?.['acukey'];
    const acId = acdto?.['id'] != undefined ? +acdto['id'] : undefined;
    const formDataId = params?.['formDataId'] != undefined ? +params['formDataId'] : undefined;

    this.logger.log(
      this.constructor.name,
      'upload answered isdraftdeleted - dropping the local draft. chainRootAcId',
      chainRootAcId,
      'acukey',
      acukey,
      'formDataId',
      formDataId,
    );

    try {
      if (chainRootAcId != undefined || acukey != undefined) {
        await this.dynFormsService.deleteDraftsForChain(chainRootAcId, acukey);
      } else if (formDataId != undefined && !isNaN(formDataId)) {
        // A document without a number range key on a backend that sends no chain root: the
        // formDataId is then the only handle on the row this upload came from.
        await this.dynFormsService.deleteFormData({ formDataId } as any);
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Could not drop the local draft of a deleted chain',
        error?.message,
      );
    }

    this.draftSyncEvents.draftDeleted$.next({ chainRootAcId, acukey, acId });
  }

  /**
   * The acdto carries real booleans, the flat keys may carry the STRINGS "true"/"false" -
   * and `Boolean("false")` is true, which would flag every upload as a conflict version.
   */
  private toBoolean(value: any): boolean {
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true' || value === '1';
    }
    return Boolean(value);
  }

  /**
   * Writes the AC infos onto the local DynFormData.
   */
  private async updateDynFormData(acInfo: UploadedAcInfo): Promise<void> {
    // APP4-948: derive the tristate cloud-sync state from the upload response. A conflict branch
    // needs a version choice on the next open; otherwise a confirmed draft owner tells us whether
    // this device still holds the baton.
    let cloudSyncState: CloudSyncState | undefined = undefined;
    if (acInfo.isConflictVersion) {
      cloudSyncState = CloudSyncState.CONFLICT;
    } else if (acInfo.draftOwnerMcId != null) {
      const myMcId = +(await this.appService.getTokenId());
      cloudSyncState =
        acInfo.draftOwnerMcId === myMcId
          ? CloudSyncState.OWNED_HERE
          : CloudSyncState.OWNED_OTHER_DEVICE;
    }

    try {
      await this.dynFormsService.updateDynFormDataACInfo(
        acInfo.formDataId,
        acInfo.acukey,
        acInfo.acId,
        acInfo.chainRootAcId,
        acInfo.acStatus,
        acInfo.acIsLocked,
        acInfo.draftOwnerEmpId,
        acInfo.draftOwnerMcId,
        acInfo.draftOwnerServiceJobId,
        cloudSyncState,
      );
    } catch (e) {
      this.logger.error(
        this.constructor.name,
        'Could not update dynformdata with acukey',
        e?.message
      );
    }
  }

  /**
   * Persists the AC the upload created, so the draft handling can work with it
   * before the next AC sync. This is a fully backend-owned record, not a local
   * placeholder - it goes into the same table the sync writes, and the next sync
   * simply replaces it.
   *
   * `acdto` is the complete AC DTO, in the exact shape of a
   * syncactivityconfirmations entry, so it goes through the very same
   * ActivityConfirmation.fromDto() as the synced records - no second mapping
   * that could drift, and nothing has to be derived from the request.
   *
   * Absent on backends that do not send the key yet. Those cannot sync ACs
   * either (APILevel.APPVERSION_4_32), so there is nothing sensible to
   * store and we skip instead of guessing a partial record together.
   */
  private async storeActivityConfirmation(acDto: any): Promise<void> {
    if (acDto == undefined) {
      this.logger.log(
        this.constructor.name,
        'Upload response has no acdto, skipping the activity confirmation'
      );
      return;
    }

    try {
      // Defensive: nested DTOs sometimes arrive as a JSON string (same handling
      // as for filledformdata in dyn-form.component.ts).
      const dto = typeof acDto === 'string' ? JSON.parse(acDto) : acDto;

      if (dto?.id == undefined || dto?.acukey == undefined) {
        this.logger.error(
          this.constructor.name,
          'acdto is missing id or acukey, skipping the activity confirmation',
          dto?.id,
          dto?.acukey
        );
        return;
      }

      await this.activityConfirmationsService.saveActivityConfirmation(
        ActivityConfirmation.fromDto(dto)
      );
    } catch (e) {
      this.logger.error(
        this.constructor.name,
        'Could not store activity confirmation from acdto',
        e?.message
      );
    }
  }
}

/**
 * @deprecated Backwards compatibility only - delegates to
 * UpdateActivityConfirmationActionHandler.
 *
 * The action target was renamed from `updateacukey` to
 * `updateactivityconfirmation`. Purely app internal (no push uses it), BUT
 * afterSendActions are persisted as JSON in the remoteActions table: a dynform
 * upload that was still queued when the app updated carries the old target.
 * Without this alias performAction() would find no handler, log 'Unknown Action
 * Target' and even show a toast - and that upload's AC infos would never reach
 * the DynFormData.
 *
 * Removable once no app version that writes `updateacukey` is in the field
 * anymore. Delete together with the 'updateacukey' entry in ActionHandlerType
 * and the provider in action-interpreter.module.ts.
 */
@Injectable({
  providedIn: 'root',
})
export class LegacyUpdateAcuKeyActionHandler implements ActionHandler {
  // Public Member
  public readonly name: ActionHandlerType = 'updateacukey';

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private updateActivityConfirmationActionHandler: UpdateActivityConfirmationActionHandler
  ) {}

  // ActionHandler
  public async executeAction(params?: any): Promise<any> {
    this.logger.log(
      this.constructor.name,
      'Handling legacy updateacukey action - upload was queued before the target rename'
    );
    return this.updateActivityConfirmationActionHandler.executeAction(params);
  }
}
